/**
 * The devices. Story V2-091.
 *
 * `voice_server_lld_v2.md` §19.2: a shell provides captured frames, a place
 * to play frames, and device events. This is the whole of the browser's
 * half — `getUserMedia`, two worklets and a graph — and it decides nothing.
 *
 * # Echo cancellation
 *
 * Asked for, and then *read back*. A browser may decline a constraint and
 * say nothing, so `getSettings()` is what the page reports rather than what
 * it requested — `voice_agent_hld_v2.md` §11.4 makes the declaration a
 * recorded fact and a metric label, and a fact that was actually a wish
 * would be worse than `unknown`.
 *
 * v2 does not act on the answer. There is no half duplex to force and no
 * barge-in decision to protect: the provider decides whether it was
 * interrupted (HLD §8.1).
 */

/** Where the worklet module is served from. `base: "./"`, so relative. */
const WORKLET_URL = "./worklet.js";

/** One line a second, so the console stays readable during a call. */
const TRACE_EVERY_MS = 1_000;

/**
 * Opens the microphone and the speaker.
 *
 * @param {{ frameMs?: number, onCaptured: (bytes: Uint8Array) => void,
 *           onDrained: (bytes: number) => void,
 *           trace?: (what: string, detail?: unknown) => void }} wiring
 */
export async function openDevices({
  frameMs = 20,
  onCaptured,
  onDrained,
  trace = () => {},
}) {
  const media = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
  });

  const track = media.getAudioTracks()[0];
  const settings = track?.getSettings?.() ?? {};
  // Three states and not a boolean. `undefined` means the browser did not
  // say, which is a different fact from "it said no" and is exactly what
  // `EchoCancellation::Unknown` is for.
  const aec =
    settings.echoCancellation === true
      ? "applied"
      : settings.echoCancellation === false
        ? "absent"
        : "unknown";

  const context = new AudioContext();
  trace("devices", {
    aec,
    sampleRate: context.sampleRate,
    state: context.state,
    // What the browser actually gave us, which is not always what was
    // asked for and is the first thing to check when a call is silent.
    track: { rate: settings.sampleRate, channels: settings.channelCount },
  });
  // A context created outside a gesture handler starts *suspended*, and
  // this one is: the click that started the call was spent on the dynamic
  // import and `getUserMedia` before we got here. A suspended context
  // renders nothing — no output to the speaker and no `process()` on the
  // capture worklet — which presents as a call that reaches `live` and is
  // silent in both directions, with nothing in any log.
  if (context.state !== "running") {
    await context.resume();
  }
  if (context.state !== "running") {
    // Still not running means the browser wants a gesture we no longer
    // have. Said plainly rather than left as silence: a caller staring at
    // a live call that cannot hear them deserves the reason.
    throw new Error(
      "the browser suspended audio; click the page and start the call again",
    );
  }
  await context.audioWorklet.addModule(WORKLET_URL);

  const source = context.createMediaStreamSource(media);
  const capture = new AudioWorkletNode(context, "voice-capture", {
    processorOptions: { frameMs },
  });
  capture.port.onmessage = (event) => onCaptured(new Uint8Array(event.data));
  source.connect(capture);
  // Into a muted gain and on to the destination, and the mute is the whole
  // point. The graph is pulled from the destination, so a node with nothing
  // downstream of it is a node whose `process()` may never be called — this
  // one had `numberOfOutputs: 0` and captured nothing. It writes silence to
  // its output either way; the gain says so out loud rather than relying on
  // that, and it is what keeps the caller from hearing themselves.
  const muted = context.createGain();
  muted.gain.value = 0;
  capture.connect(muted);
  muted.connect(context.destination);

  const playback = new AudioWorkletNode(context, "voice-playback", {
    numberOfInputs: 0,
    outputChannelCount: [1],
  });
  // Totals rather than a line per quantum: the worklet reports every 128
  // samples, which is four hundred times a second.
  const device = { givenBytes: 0, drainedBytes: 0, at: 0 };
  const beat = () => {
    const now = performance.now();
    if (now - device.at < TRACE_EVERY_MS) {
      return;
    }
    device.at = now;
    // `given` minus `drained` is what the speaker still holds. If it grows
    // without bound the device is not consuming; if it sits at zero while
    // frames arrive, the audio is being played and the fault is upstream.
    trace("device", {
      givenBytes: device.givenBytes,
      drainedBytes: device.drainedBytes,
      outstandingBytes: device.givenBytes - device.drainedBytes,
    });
  };

  playback.port.onmessage = (event) => {
    device.drainedBytes += event.data;
    beat();
    onDrained(event.data);
  };
  playback.connect(context.destination);

  // Two taps, for the two ribbons the call screen draws. They are read on
  // the animation frame and never on the audio thread, and an `AnalyserNode`
  // that nobody reads costs one FFT that is never computed.
  const inputLevel = context.createAnalyser();
  inputLevel.fftSize = 256;
  source.connect(inputLevel);
  const outputLevel = context.createAnalyser();
  outputLevel.fftSize = 256;
  playback.connect(outputLevel);

  // Suspended until the core says to capture. A page that opened the
  // microphone and left it running while a call connected would show a
  // recording indicator for a call that has not started.
  //
  // The tracks are *made* to match, and that is not a formality:
  // `getUserMedia` hands them back enabled, so a `capturing` that started
  // `false` beside enabled tracks meant `capture(false)` took the
  // early-return and left the microphone live — a suspended call with the
  // recording indicator still on.
  for (const target of media.getAudioTracks()) {
    target.enabled = false;
  }
  let capturing = false;

  return {
    /** What the page declares in `hello`. */
    aec,
    /** The rate the graph actually runs at, which `hello` must declare. */
    sampleRate: context.sampleRate,
    /** What the microphone is hearing, for the call screen's ribbon. */
    inputLevel,
    /** What the speaker is playing. */
    outputLevel,
    capture(on) {
      if (on === capturing) {
        return;
      }
      capturing = on;
      for (const target of media.getAudioTracks()) {
        target.enabled = on;
      }
    },
    play(bytes) {
      // Transferred rather than copied: this runs fifty times a second and
      // the buffer is the core's, which will not touch it again.
      const buffer = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      );
      device.givenBytes += buffer.byteLength;
      playback.port.postMessage(buffer, [buffer]);
    },
    flush() {
      trace("device.flush", {
        outstandingBytes: device.givenBytes - device.drainedBytes,
      });
      // Whatever the speaker had is gone, so the two totals are level
      // again — not resetting would leave every line after a barge-in
      // reporting a backlog that no longer exists.
      device.drainedBytes = device.givenBytes;
      playback.port.postMessage("flush");
    },
    async close() {
      for (const target of media.getTracks()) {
        target.stop();
      }
      capture.port.onmessage = null;
      playback.port.onmessage = null;
      await context.close();
    },
  };
}
